import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { Megaphone, Plus, Users, Send, BarChart2, Search, TrendingUp } from 'lucide-react';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Button } from '../../components/ui/Button';
import MetaBulkCampaignForm from './MetaBulkCampaignForm';
import MetaCampaignDetailsModal from './MetaCampaignDetailsModal';
import { cn } from '../../utils/cn';

const MetaCampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [search, setSearch] = useState('');

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await api.get('/meta/campaigns');
      setCampaigns(res.data);
    } catch (err) {
      alertService.toast('error', 'Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (campaignData) => {
    try {
      await api.post('/meta/campaigns', campaignData);
      alertService.toast('success', campaignData.scheduledAt ? 'Campaign scheduled successfully' : 'Campaign created successfully');
      fetchCampaigns();
    } catch (err) {
      // Re-thrown deliberately — the form modal awaits this call and only
      // closes/clears the user's filled-in campaign on success. Swallowing
      // the error here previously meant a failed campaign (e.g. template
      // not found) still closed the modal as if it had worked, silently
      // discarding everything the user had entered.
      alertService.toast('error', err.response?.data?.error || 'Failed to create campaign');
      throw err;
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Previously hardcoded to '0'/'0%' regardless of real campaign activity —
  // MetaCampaign already tracks real sent/read counts per campaign
  // (populated by the send worker), just never summed here.
  const totalSent = campaigns.reduce((sum, c) => sum + (c.stats?.sent || 0), 0);
  const totalDelivered = campaigns.reduce((sum, c) => sum + (c.stats?.delivered || c.stats?.sent || 0), 0);
  const totalRead = campaigns.reduce((sum, c) => sum + (c.stats?.read || 0), 0);
  const readRate = totalDelivered > 0 ? Math.round((totalRead / totalDelivered) * 100) : 0;

  const stats = [
    { label: 'Active Campaigns', value: campaigns.filter(c => c.status === 'processing').length, icon: Megaphone, meta: 'Live' },
    { label: 'Total Campaigns', value: campaigns.length, icon: Users },
    { label: 'Messages Sent', value: totalSent.toLocaleString(), icon: Send, meta: 'Messages' },
    { label: 'Read Rate', value: `${readRate}%`, icon: TrendingUp, meta: 'Performance' },
  ];

  const filteredCampaigns = search
    ? campaigns.filter((c) => c.name?.toLowerCase().includes(search.toLowerCase()))
    : campaigns;

  return (
    <DashboardModuleShell
      title="Meta Campaigns"
      subtitle="Create and manage WhatsApp Business marketing campaigns."
      icon={Megaphone}
      stats={stats}
      actions={
        <div className="flex items-center gap-3">
          <Button
            onClick={() => setShowForm(true)}
            className="h-9 px-4 rounded text-[13px] font-bold"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[12px] font-bold text-[#706E6B] uppercase tracking-wider">Campaign History</h3>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#706E6B]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search campaigns…"
                className="h-9 pl-9 pr-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[12px] outline-none focus:bg-white focus:border-[#0176D3] transition-all w-48"
              />
            </div>
          </div>
        </div>

        <div className="sf-card">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 animate-pulse">
              <Megaphone className="w-10 h-10 text-[#D8DDE6] mb-4" />
              <div className="h-4 w-32 bg-[#F4F6F9] rounded mb-2" />
              <div className="h-3 w-48 bg-[#F4F6F9] rounded" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="w-16 h-16 rounded-full bg-[#F4F6F9] flex items-center justify-center mx-auto mb-4 border border-[#D8DDE6]">
                <Megaphone className="w-8 h-8 text-[#706E6B]" />
              </div>
              <h3 className="text-[15px] font-bold text-[#080707] mb-2">No campaigns yet</h3>
              <p className="text-[13px] text-[#706E6B] mb-6 max-w-sm mx-auto">
                Your campaign list is currently empty. Create a new campaign to start reaching your audience.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="primary" icon={Plus} onClick={() => setShowForm(true)}>New Campaign</Button>
              </div>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="text-center py-16 px-6">
              <p className="text-[13px] text-[#706E6B]">No campaigns match "{search}".</p>
            </div>
          ) : (
            <div className="sf-table-container overflow-x-auto">
              <table className="sf-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Audience</th>
                    <th>Created</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCampaigns.map(c => (
                    <tr key={c._id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-[#0176D3]/10 flex items-center justify-center text-[#0176D3]">
                            <Megaphone size={14} />
                          </div>
                          <div>
                            <p className="text-[13px] font-bold text-[#080707]">{c.name}</p>
                            <p className="text-[11px] text-[#706E6B] mt-0.5">ID: {c._id.slice(-6)}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={cn(
"sf-badge",
                          c.status === 'completed' ? "sf-badge-success" :
                          c.status === 'completed_with_errors' ? "sf-badge-warning" :
                          c.status === 'processing' ? "sf-badge-manager" :
                          c.status === 'scheduled' ? "sf-badge-manager" : "sf-badge-neutral"
                        )}>
                          {c.status === 'completed_with_errors' ? 'completed (errors)' : c.status}
                        </span>
                      </td>
                      <td className="text-[12px] font-bold text-[#080707]">
                        {c.stats?.total || 0} <span className="text-[#706E6B] font-medium ml-1">leads</span>
                      </td>
                      <td className="text-[12px] text-[#706E6B]">
                        {new Date(c.createdAt).toLocaleDateString()}
                      </td>
                      <td className="text-right">
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={BarChart2}
                          onClick={() => setSelectedCampaignId(c._id)}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <MetaBulkCampaignForm
          onClose={() => setShowForm(false)}
          onSave={handleCreateCampaign}
        />
      )}

      {selectedCampaignId && (
        <MetaCampaignDetailsModal
          campaignId={selectedCampaignId}
          onClose={() => setSelectedCampaignId(null)}
        />
      )}
    </DashboardModuleShell>
  );
};

export default MetaCampaignsPage;
